import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { downloadExport, postDrill, type DrillRequest } from '../api';
import { sampleCalls, sampleOrders, sampleWork } from '../mockData';
import { useReport } from '../ReportContext';
import { inr, num } from '../format';
import { BUCKETS } from '../theme';
import type { DrillContext, OrderBucketKey } from '../types';
import { toastMessage } from '@/utils/toast-message';
import BucketChip from './BucketChip';
import SourceBadge from './SourceBadge';

const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700';
const td = 'px-3 py-2 text-sm text-slate-700 dark:text-slate-200';
const PAGE = 100;

type Rec = Record<string, unknown>;
const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const when = (v: unknown) => (v ? new Date(String(v)).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const bucketOf = (v: unknown): OrderBucketKey => (v && String(v) in BUCKETS ? (String(v) as OrderBucketKey) : 'none');
const mmss = (sec: number) => (sec ? `${Math.floor(sec / 60)}:${String(Math.round(sec) % 60).padStart(2, '0')}` : '—');

/** Side panel behind any number: the records that add up to it. Live mode reads them from the server page by page. */
const DrilldownDrawer: React.FC<{ ctx: DrillContext | null; onClose: () => void }> = ({ ctx, onClose }) => {
  const { mode, ranFilters: filters, resolveAgent, agentId: filterAgentId } = useReport();
  const [rows, setRows] = useState<Rec[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback((p: number): { agentId: number | null; req: DrillRequest } | null => {
    if (!ctx) return null;
    const { agentId, noAgent } = resolveAgent(ctx.agent);
    return {
      agentId: agentId ?? filterAgentId,
      req: {
        kind: ctx.kind, bucket: ctx.bucket === 'all' || ctx.bucket === 'none' ? null : ctx.bucket,
        no_bucket: ctx.bucket === 'none', no_agent: noAgent,
        worked: ctx.worked && ctx.worked !== 'all' ? ctx.worked : null,
        status: ctx.orderStatus && ctx.orderStatus !== 'all' ? ctx.orderStatus : null,
        basis: ctx.basis ?? null, category: ctx.category ?? null, product: ctx.product ?? null,
        // Every number on these tabs counts agent orders, so "all agents" must not open website orders.
        agents_only: ctx.kind === 'orders' && !noAgent && (agentId ?? filterAgentId) == null,
        outcome: ctx.callOutcome && ctx.callOutcome !== 'all' ? ctx.callOutcome : null,
        hour: ctx.hour ?? null,
        page: p, page_size: PAGE,
      },
    };
  }, [ctx, resolveAgent, filterAgentId]);

  // First page whenever a different number is opened.
  useEffect(() => {
    if (!ctx) return;
    setRows([]); setPage(1); setError(null);
    if (mode === 'sample') {
      const sample = ctx.kind === 'work' ? sampleWork(ctx) : ctx.kind === 'orders' ? sampleOrders(ctx) : sampleCalls(ctx);
      setRows(sample as unknown as Rec[]); setTotal(ctx.total);
      return;
    }
    const r = request(1);
    if (!r) return;
    let alive = true;
    setLoading(true);
    postDrill(filters, r.agentId, r.req)
      .then(res => { if (alive) { setRows(res.rows); setTotal(res.total); } })
      .catch(() => { if (alive) setError('The records could not be loaded'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ctx, mode]);

  useEffect(() => {
    if (!ctx) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctx, onClose]);

  if (!ctx) return null;

  const loadMore = async () => {
    const r = request(page + 1);
    if (!r) return;
    setLoading(true);
    try {
      const res = await postDrill(filters, r.agentId, r.req);
      setRows(prev => [...prev, ...res.rows]); setPage(page + 1);
    } catch { setError('The next records could not be loaded'); } finally { setLoading(false); }
  };

  // The file holds every record behind the number, not only the rows previewed here.
  const download = async () => {
    if (mode === 'sample') { toastMessage('Downloads read real data. Switch off sample mode to download.', 'info'); return; }
    const r = request(1);
    if (!r) return;
    const dataset = ctx.kind === 'work' ? (ctx.bucket === 'abc' ? 'abc_events' : 'work_items') : ctx.kind === 'orders' ? 'orders' : 'calls';
    try {
      const n = await downloadExport(dataset, filters, r.agentId, r.req.no_agent, {
        bucket: r.req.bucket ?? null, worked: r.req.worked ?? null, status: r.req.status ?? null, basis: r.req.basis ?? null, category: r.req.category ?? null, product: r.req.product ?? null, agents_only: r.req.agents_only ?? false, outcome: r.req.outcome ?? null, no_bucket: r.req.no_bucket,
      });
      toastMessage(`Downloaded ${num(n)} rows`, 'success');
    } catch { toastMessage('The download failed', 'error'); }
  };

  const shown = rows.length;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-slate-900" role="dialog" aria-label={ctx.title}>
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-violet-50 via-sky-50 to-emerald-50 px-5 py-4 dark:border-slate-700 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800">
          <div>
            <h2 className="text-base font-semibold text-slate-800 dark:text-white">{ctx.title}</h2>
            {ctx.subtitle && <p className="mt-0.5 text-xs text-slate-700 dark:text-slate-300">{ctx.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {ctx.hour === undefined && <button type="button" onClick={download}
              className="flex items-center gap-1.5 rounded-lg bg-white/80 px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-white dark:bg-slate-700 dark:text-violet-100">
              <Download className="h-4 w-4" /> Download all {num(mode === 'live' ? total : ctx.total)}
            </button>}
            <button type="button" onClick={onClose} aria-label="Close"
              className="rounded-lg p-1.5 text-slate-700 hover:bg-white/70 dark:hover:bg-slate-700"><X className="h-5 w-5" /></button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {mode === 'sample' && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
              Sample rows. With live data every number opens the real records behind it.
            </p>
          )}
          {error && <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800">{error}</p>}
          <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
            {ctx.kind === 'calls' && (
              <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-800"><tr>
                  <th className={th}>Call</th><th className={th}>When</th><th className={th}>Agent</th><th className={th}>Work</th>
                  <th className={th}>Customer</th><th className={th}>Outcome</th><th className={`${th} text-right`}>Talk</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {rows.map((r, i) => {
                    const live = mode === 'live';
                    const outcome = live ? s(r.outcome) : s(r.outcome);
                    const talk = Number(live ? r.talk_seconds : r.talk) || 0;
                    return (
                      <tr key={i}>
                        <td className={`${td} font-mono text-xs`}>{live ? s(r.call_id) : s(r.id)}</td>
                        <td className={td}>{live ? when(r.called_at) : s(r.calledAt)}</td>
                        <td className={td}>{s(r.agent) || 'Unmatched'}</td>
                        <td className={td}><BucketChip bucket={bucketOf(r.bucket)} label={bucketOf(r.bucket) === 'none' ? 'No open work' : undefined} /></td>
                        <td className={td}>{live ? `${s(r.customer_name)} ${s(r.customer_phone)}` : s(r.customer)}</td>
                        <td className={td}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${outcome === 'Connected'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200'
                            : 'bg-rose-100 text-rose-800 dark:bg-rose-400/15 dark:text-rose-200'}`}>{outcome}</span>
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{mmss(talk)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {ctx.kind === 'work' && (
              <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-800"><tr>
                  <th className={th}>Work ID</th><th className={th}>Customer</th><th className={th}>Agent</th><th className={th}>Bucket</th>
                  <th className={th}>Received</th><th className={th}>State</th><th className={th}>Source</th>{mode === 'sample' && <th className={th}>Disposition</th>}
                </tr></thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {rows.map((r, i) => {
                    const live = mode === 'live';
                    const worked = live ? Boolean(r.worked) : r.state === 'Worked';
                    return (
                      <tr key={i}>
                        <td className={`${td} font-mono text-xs`}>{live ? `${s(r.bucket).toUpperCase()}-${s(r.item_id)}` : s(r.id)}</td>
                        <td className={td}>{live ? `${s(r.customer_name)} ${s(r.customer_phone)}` : s(r.phone)}</td>
                        <td className={td}>{s(r.agent) || 'Unassigned'}</td>
                        <td className={td}><BucketChip bucket={bucketOf(r.bucket)} /></td>
                        <td className={td}>{live ? when(r.received_at) : s(r.receivedAt)}</td>
                        <td className={td}><span className={worked ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600'}>{worked ? 'Worked' : 'Unworked'}</span></td>
                        <td className={td}>{worked ? <SourceBadge source={live ? s(r.source) || 'UNSPLIT' : s(r.source)} /> : '—'}</td>
                        {!live && <td className={td}>{s(r.disposition)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {ctx.kind === 'orders' && (
              <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-800"><tr>
                  <th className={th}>Order</th><th className={th}>Credited agent</th><th className={th}>Bucket</th><th className={th}>Product</th>
                  <th className={th}>Attribution</th><th className={th}>Status</th><th className={`${th} text-right`}>Value</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {rows.map((r, i) => {
                    const live = mode === 'live';
                    const cancelled = live ? Boolean(r.cancelled) : r.status === 'Cancelled';
                    const attribution = live ? ({ agent_tag: 'Agent Tag', discount_code: 'Coupon' } as Record<string, string>)[s(r.attribution)] ?? '—' : s(r.attribution);
                    return (
                      <tr key={i}>
                        <td className={`${td} font-mono text-xs`}>{live ? s(r.order_name) : s(r.order)}</td>
                        <td className={td}>{live ? s(r.credited_agent) || 'No agent tag' : s(r.agent)}</td>
                        <td className={td}><BucketChip bucket={bucketOf(r.bucket)} /></td>
                        <td className={`${td} max-w-[14rem] truncate`} title={live ? s(r.products) : s(r.product)}>{live ? s(r.products) : s(r.product)}</td>
                        <td className={td}>{attribution}</td>
                        <td className={td}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cancelled
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-400/15 dark:text-rose-200'
                            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200'}`}>{cancelled ? 'Cancelled' : 'Valid'}</span>
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{inr(Number(live ? r.order_value : r.value))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {shown === 0 && !loading && <p className="p-6 text-center text-sm text-slate-600">Nothing behind this number.</p>}
            {loading && <p className="flex items-center justify-center gap-2 p-6 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
            <span>Showing {num(shown)} of {num(mode === 'live' ? total : ctx.total)}{mode === 'sample' ? ' (sample)' : ''}</span>
            {mode === 'live' && shown < total && (
              <button type="button" onClick={loadMore} disabled={loading}
                className="rounded-lg border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200">
                Load {Math.min(PAGE, total - shown)} more
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default DrilldownDrawer;
